import { useState, useCallback } from 'react'
import { Aircraft, Transaction, Event } from '../types/intellijetnet'
import { MarketCustomColumn, AircraftTransaction, TransactionOverride } from '../types/database'
import { ConsolidatedTransaction } from '../utils/transaction-consolidation'
import { ExportOptions } from '../components/pdf-export/ExportOptionsModal'
import { RelativeValueBreakdown } from '../components/pdf-export/PDFPreviewModal'
import { MarketStatistics, calculateMarketStatistics } from '../utils/market-statistics'
import { HiddenTransactionsService } from '../services/hidden-transactions.service'
import { AircraftMakesService } from '../services/intellijetnet/aircraft-makes.service'
import { supabase } from '../lib/supabase'
import { SoldAircraftItem } from './useMarketSoldData'
import { OffMarketAircraftItem } from './useMarketOffMarket'

export interface PreviewData {
  tables: any[]
  overrides: Map<number, any>
  options: Map<number, any>
  relativeValues: Map<number, number | null>
  relativeValueBreakdowns: Map<number, RelativeValueBreakdown>
  missingABB: Set<number>
  exportOptions: ExportOptions
  companies: Map<number, { company_name: string }>
  transactionOverrides: Map<string, TransactionOverride>
  transactionOptionDetails: Map<string, { name: string; value: number }>
  manufacturer?: string | null
  marketStatistics?: MarketStatistics
}

interface UseMarketPDFExportParams {
  modelId: string | undefined
  modelInfo: { model_id: number; model_name: string; make_name: string } | null
  aircraft: Aircraft[]
  recentlySoldAircraft: SoldAircraftItem[]
  removedFromMarketAircraft: { aircraft: Aircraft; event: Event | null }[]
  offMarketAircraftData: OffMarketAircraftItem[]
  filteredOffMarketForSale: OffMarketAircraftItem[]
  hiddenIdentifiers: Set<string>
  transactions: Transaction[] | null
  customColumns: MarketCustomColumn[]
}

export function useMarketPDFExport({
  modelId,
  modelInfo,
  aircraft,
  recentlySoldAircraft,
  removedFromMarketAircraft,
  offMarketAircraftData,
  filteredOffMarketForSale,
  hiddenIdentifiers,
  transactions,
  customColumns,
}: UseMarketPDFExportParams) {
  const [showExportOptionsModal, setShowExportOptionsModal] = useState(false)
  const [showPDFPreviewModal, setShowPDFPreviewModal] = useState(false)
  const [previewData, setPreviewData] = useState<PreviewData | null>(null)

  const handleExportPDF = useCallback(() => {
    setShowExportOptionsModal(true)
  }, [])

  const prepareExportData = useCallback(async (exportOptions: ExportOptions): Promise<PreviewData | null> => {
    if (!modelInfo) return null

    const historicalMonths = exportOptions.historicalMonths || 12
    const cutoffDate = new Date()
    cutoffDate.setMonth(cutoffDate.getMonth() - historicalMonths)
    const cutoffDateStr = cutoffDate.toISOString().split('T')[0]

    // Get aircraft IDs that have been manually moved to other containers
    const modelIdNumeric = parseInt(modelId!, 10)
    const { TemporaryAircraftStateService } = await import('../services/temporary-aircraft-state.service')

    const excludedFromForSale = await TemporaryAircraftStateService.getAircraftIdsToExclude(modelIdNumeric, 'for_sale')
    const excludedFromForSaleSet = new Set(excludedFromForSale)

    const excludedFromRemoved = await TemporaryAircraftStateService.getAircraftIdsToExclude(modelIdNumeric, 'removed_from_market')
    const excludedFromRemovedSet = new Set(excludedFromRemoved)

    const excludedFromSold = await TemporaryAircraftStateService.getAircraftIdsToExclude(modelIdNumeric, 'sold')
    const excludedFromSoldSet = new Set(excludedFromSold)

    const tempForSaleAircraft = await TemporaryAircraftStateService.getTemporaryAircraftForContainer(modelIdNumeric, 'for_sale')
    const tempSoldAircraft = await TemporaryAircraftStateService.getTemporaryAircraftForContainer(modelIdNumeric, 'sold')
    const tempRemovedAircraft = await TemporaryAircraftStateService.getTemporaryAircraftForContainer(modelIdNumeric, 'removed_from_market')

    // Filter sold data by date and exclusions
    const filteredSoldData = recentlySoldAircraft.filter(item => {
      if (excludedFromSoldSet.has(item.aircraft.aircraft_id)) return false
      const txId = HiddenTransactionsService.getTransactionIdentifier(item.aircraftTransaction, item.transaction)
      if (txId && hiddenIdentifiers.has(txId)) return false
      const dateSold = item.aircraftTransaction?.transaction_date || item.transaction?.trans_date
      if (!dateSold) return true
      return dateSold >= cutoffDateStr
    })

    const forSaleAircraftIds = new Set(aircraft.map(a => a.aircraft_id))

    const filteredRemovedData = removedFromMarketAircraft.filter(item => {
      if (forSaleAircraftIds.has(item.aircraft.aircraft_id)) return false
      if (excludedFromRemovedSet.has(item.aircraft.aircraft_id)) return false
      if (!item.event?.date) return true
      return item.event.date >= cutoffDateStr
    })

    const filteredSoldOffMarket = offMarketAircraftData.filter(item => {
      if (item.offMarket.market_category !== 'recently_sold') return false
      const dateRef = item.offMarket.updated_at || item.offMarket.created_at
      if (!dateRef) return true
      return dateRef >= cutoffDateStr
    })

    const filteredRemovedOffMarket = offMarketAircraftData.filter(item => {
      if (item.offMarket.market_category !== 'removed_from_market') return false
      const dateRef = item.offMarket.updated_at || item.offMarket.created_at
      if (!dateRef) return true
      return dateRef >= cutoffDateStr
    })

    const filteredForSaleAircraft = aircraft.filter(a => !excludedFromForSaleSet.has(a.aircraft_id))

    const tempForSaleFormatted = tempForSaleAircraft.map(temp => (temp.aircraft_data as Aircraft))
    const tempSoldFormatted = tempSoldAircraft.map(temp => ({
      aircraft: temp.aircraft_data as Aircraft,
      transaction: null as ConsolidatedTransaction | null,
      aircraftTransaction: temp.override_data || null,
      isTemporary: true
    }))
    const tempRemovedFormatted = tempRemovedAircraft.map(temp => ({
      aircraft: temp.aircraft_data as Aircraft,
      event: null as Event | null,
      isTemporary: true
    }))


    const marketData = {
      forSaleAircraft: [...filteredForSaleAircraft, ...tempForSaleFormatted],
      forSaleOffMarket: filteredOffMarketForSale.map(item => ({ ...item })),
      soldData: [...filteredSoldData.map(item => ({ ...item })), ...tempSoldFormatted],
      soldOffMarket: filteredSoldOffMarket.map(item => ({ ...item })),
      removedData: [...filteredRemovedData.map(item => ({ ...item })), ...tempRemovedFormatted],
      removedOffMarket: filteredRemovedOffMarket.map(item => ({ ...item }))
    }

    const getAllAircraftIds = (data: typeof marketData) => {
      const ids = new Set<number>()
      data.forSaleAircraft.forEach(a => ids.add(a.aircraft_id))
      data.forSaleOffMarket.forEach(item => ids.add(item.aircraft.aircraft_id))
      data.soldData.forEach(item => ids.add(item.aircraft.aircraft_id))
      data.soldOffMarket.forEach(item => ids.add(item.aircraft.aircraft_id))
      data.removedData.forEach(item => ids.add(item.aircraft.aircraft_id))
      data.removedOffMarket.forEach(item => ids.add(item.aircraft.aircraft_id))
      return Array.from(ids)
    }

    const allAircraftIds = getAllAircraftIds(marketData)

    // Helper: Fetch aircraft options
    const fetchAircraftOptions = async (aircraftIds: number[]) => {
      if (aircraftIds.length === 0) return new Map<number, Array<{ name: string; value: number }>>()
      try {
        const { data: aircraftData, error: aircraftError } = await supabase
          .from('aircraft').select('aircraft_id, options').in('aircraft_id', aircraftIds)
        if (aircraftError) throw aircraftError
        if (!aircraftData || aircraftData.length === 0) return new Map<number, Array<{ name: string; value: number }>>()

        const allOptionIds = new Set<string>()
        aircraftData.forEach((ac: any) => {
          if (ac.options && Array.isArray(ac.options)) {
            ac.options.forEach((optionId: string) => allOptionIds.add(optionId))
          }
        })
        if (allOptionIds.size === 0) return new Map<number, Array<{ name: string; value: number }>>()

        const { data: optionsData, error: optionsError } = await supabase
          .from('options').select('*').in('option_id', Array.from(allOptionIds))
        if (optionsError) throw optionsError

        const optionsMap = new Map<string, { name: string; value: number }>()
        optionsData?.forEach((option: any) => {
          optionsMap.set(option.option_id, { name: option.name, value: option.value })
        })

        const resultMap = new Map<number, Array<{ name: string; value: number }>>()
        aircraftData.forEach((ac: any) => {
          const acOptions: Array<{ name: string; value: number }> = []
          if (ac.options && Array.isArray(ac.options)) {
            ac.options.forEach((optionId: string) => {
              const option = optionsMap.get(optionId)
              if (option) acOptions.push(option)
            })
          }
          resultMap.set(ac.aircraft_id, acOptions)
        })
        return resultMap
      } catch (error) {
        return new Map<number, Array<{ name: string; value: number }>>()
      }
    }

    // Helper: Calculate relative values
    const calculateRelativeValues = async (
      aircraftIds: number[],
      numModelId: number,
      overridesMap: Map<number, any>,
      optionsMap: Map<number, Array<{ name: string; value: number }>>
    ): Promise<{ values: Map<number, number | null>; breakdowns: Map<number, RelativeValueBreakdown>; missingABB: Set<number> }> => {
      if (aircraftIds.length === 0) {
        return { values: new Map(), breakdowns: new Map(), missingABB: new Set() }
      }
      try {
        const { data: marketSettings, error: settingsError } = await supabase
          .from('market_settings')
          .select('fleet_avg_aftt, maintenance_cost, use_manufacturing_date')
          .eq('model_id', numModelId)
          .single()

        if (settingsError && settingsError.code !== 'PGRST116') { /* continue with defaults */ }

        if (!marketSettings || marketSettings.fleet_avg_aftt === null || marketSettings.maintenance_cost === null) {
          const emptyMap = new Map<number, number | null>()
          aircraftIds.forEach(id => emptyMap.set(id, null))
          return { values: emptyMap, breakdowns: new Map(), missingABB: new Set() }
        }

        const useManufacturingDate = marketSettings.use_manufacturing_date ?? false

        const allAircraftData: Array<{ aircraft_id: number; yearmfr: number | null; yeardlv: number | null; aftt: number | null; transDate?: string | null }> = []
        marketData.forSaleAircraft.forEach(a => {
          allAircraftData.push({ aircraft_id: a.aircraft_id, yearmfr: a.yearmfr, yeardlv: a.yeardlv, aftt: a.aftt, transDate: null })
        })
        marketData.forSaleOffMarket.forEach(item => {
          allAircraftData.push({ aircraft_id: item.aircraft.aircraft_id, yearmfr: item.aircraft.yearmfr, yeardlv: item.aircraft.yeardlv, aftt: item.aircraft.aftt, transDate: null })
        })
        marketData.soldData.forEach(item => {
          const transDate = item.aircraftTransaction?.transaction_date || item.transaction?.trans_date || null
          allAircraftData.push({ aircraft_id: item.aircraft.aircraft_id, yearmfr: item.aircraft.yearmfr, yeardlv: item.aircraft.yeardlv, aftt: item.aircraft.aftt, transDate })
        })
        marketData.soldOffMarket.forEach(item => {
          allAircraftData.push({ aircraft_id: item.aircraft.aircraft_id, yearmfr: item.aircraft.yearmfr, yeardlv: item.aircraft.yeardlv, aftt: item.aircraft.aftt, transDate: null })
        })
        marketData.removedData.forEach(item => {
          allAircraftData.push({ aircraft_id: item.aircraft.aircraft_id, yearmfr: item.aircraft.yearmfr, yeardlv: item.aircraft.yeardlv, aftt: item.aircraft.aftt, transDate: null })
        })
        marketData.removedOffMarket.forEach(item => {
          allAircraftData.push({ aircraft_id: item.aircraft.aircraft_id, yearmfr: item.aircraft.yearmfr, yeardlv: item.aircraft.yeardlv, aftt: item.aircraft.aftt, transDate: null })
        })

        const yearModels = [...new Set(allAircraftData.map(a =>
          useManufacturingDate ? a.yearmfr : a.yeardlv
        ).filter(y => y !== null))] as number[]

        if (yearModels.length === 0) {
          const emptyMap = new Map<number, number | null>()
          aircraftIds.forEach(id => emptyMap.set(id, null))
          return { values: emptyMap, breakdowns: new Map(), missingABB: new Set() }
        }

        const [abbResult, activeEmpData] = await Promise.all([
          supabase
            .from('abb')
            .select('year_model, year, quarter, value')
            .eq('model_id', numModelId)
            .in('year_model', yearModels)
            .order('year', { ascending: false })
            .order('quarter', { ascending: false }),
          import('../services/market-emps.service').then(({ MarketEMPsService }) =>
            MarketEMPsService.getActiveByModelId(numModelId)
          )
        ])

        const abbData = abbResult.data || []
        const currentYear = new Date().getFullYear()
        const resultMap = new Map<number, number | null>()
        const breakdownsMap = new Map<number, RelativeValueBreakdown>()
        const missingABBSet = new Set<number>()

        allAircraftData.forEach(acData => {
          try {
            const override = overridesMap.get(acData.aircraft_id)
            const effectiveAftt = override?.aftt ?? acData.aftt
            const yearForABB = useManufacturingDate ? acData.yearmfr : acData.yeardlv

            if (!yearForABB || effectiveAftt === null || effectiveAftt === undefined) {
              resultMap.set(acData.aircraft_id, null)
              return
            }

            let abbValue: number | null = null
            const abbMatchesForYearModel = abbData.filter((d: any) => d.year_model === yearForABB)

            if (acData.transDate) {
              const transDate = new Date(acData.transDate)
              const transYear = transDate.getFullYear()
              const transQuarter = Math.floor(transDate.getMonth() / 3) + 1
              const exactMatch = abbMatchesForYearModel.find(
                (d: any) => d.year === transYear && d.quarter === transQuarter
              )
              if (exactMatch) {
                abbValue = exactMatch.value
              } else if (abbMatchesForYearModel.length > 0) {
                const transDateValue = transYear * 4 + transQuarter
                const sortedByDistance = [...abbMatchesForYearModel].sort((a: any, b: any) => {
                  const distA = Math.abs((a.year * 4 + a.quarter) - transDateValue)
                  const distB = Math.abs((b.year * 4 + b.quarter) - transDateValue)
                  return distA - distB
                })
                abbValue = sortedByDistance[0].value
              }
            } else {
              if (abbMatchesForYearModel.length > 0) {
                abbValue = abbMatchesForYearModel[0].value
              }
            }

            if (!abbValue) {
              resultMap.set(acData.aircraft_id, null)
              missingABBSet.add(acData.aircraft_id)
              return
            }

            const expectedAFTT = marketSettings.fleet_avg_aftt * (currentYear - yearForABB)
            const afttDifference = expectedAFTT - effectiveAftt
            const maintenanceAdjustment = afttDifference * marketSettings.maintenance_cost

            const aircraftOptions = optionsMap.get(acData.aircraft_id) || []
            const optionsTotal = aircraftOptions.reduce((sum, option) => sum + option.value, 0)

            let empCostAdjustment = 0
            const aircraftEMP = override?.emp
            if (!aircraftEMP || aircraftEMP.trim() === '') {
              if (activeEmpData && activeEmpData.cost_per_hour !== null && activeEmpData.cost_per_hour !== undefined && activeEmpData.cost_per_hour > 0) {
                empCostAdjustment = effectiveAftt * activeEmpData.cost_per_hour
              }
            }

            const relativeValue = abbValue + maintenanceAdjustment + optionsTotal - empCostAdjustment
            resultMap.set(acData.aircraft_id, Math.round(relativeValue))

            breakdownsMap.set(acData.aircraft_id, {
              total: Math.round(relativeValue),
              abbValue,
              optionsTotal,
              maintenanceAdjustment: Math.round(maintenanceAdjustment),
              empCostAdjustment: Math.round(empCostAdjustment)
            })
          } catch (err) {
            resultMap.set(acData.aircraft_id, null)
          }
        })

        return { values: resultMap, breakdowns: breakdownsMap, missingABB: missingABBSet }
      } catch (error) {
        const emptyMap = new Map<number, number | null>()
        aircraftIds.forEach(id => emptyMap.set(id, null))
        return { values: emptyMap, breakdowns: new Map(), missingABB: new Set() }
      }
    }

    // Fetch additional data needed for preview/export
    const [overrides, options] = await Promise.all([
      import('../services/overrides.service').then(({ OverridesService }) =>
        OverridesService.getByAircraftIds(allAircraftIds)
      ),
      fetchAircraftOptions(allAircraftIds)
    ])

    // Fetch company data for owner/broker display
    const companyIds = new Set<number>()
    overrides.forEach(override => {
      if (override.owner && !override.owner_custom) companyIds.add(override.owner)
      if (override.broker && !override.broker_custom) companyIds.add(override.broker)
    })

    const companies = new Map<number, { company_name: string }>()
    if (companyIds.size > 0) {
      try {
        const { CompaniesService } = await import('../services/intellijetnet/companies.service')
        const companyPromises = Array.from(companyIds).map(id => CompaniesService.getById(id))
        const companyResults = await Promise.all(companyPromises)
        companyResults.forEach(company => {
          if (company) {
            companies.set(company.company_id, { company_name: company.company_name })
          }
        })
      } catch (error) {
        // Error fetching companies for PDF preview
      }
    }

    // Prepare table data structure
    const tables = [
      {
        type: 'for-sale' as const,
        title: 'Currently on Market',
        data: [
          ...marketData.forSaleAircraft.map(a => ({ aircraft: a, source: 'api' as const })),
          ...marketData.forSaleOffMarket.map(item => ({
            aircraft: item.aircraft, source: 'offMarket' as const, offMarket: item.offMarket
          }))
        ]
      },
      {
        type: 'sold' as const,
        title: 'Recently Sold',
        data: [
          ...marketData.soldData.map(item => ({
            aircraft: item.aircraft, source: 'api' as const,
            transaction: item.transaction, aircraftTransaction: item.aircraftTransaction
          })),
          ...marketData.soldOffMarket.map(item => ({
            aircraft: item.aircraft, source: 'offMarket' as const,
            offMarket: item.offMarket, transaction: null
          }))
        ]
      },
      {
        type: 'removed' as const,
        title: 'Removed from Market',
        data: [
          ...marketData.removedData.map(item => ({
            aircraft: item.aircraft, source: 'api' as const, event: item.event
          })),
          ...marketData.removedOffMarket.map(item => ({
            aircraft: item.aircraft, source: 'offMarket' as const,
            offMarket: item.offMarket, event: null
          }))
        ]
      }
    ].filter(table => table.data.length > 0)

    // Convert overrides array to Map
    const overridesMap = new Map<number, any>()
    overrides.forEach(override => {
      overridesMap.set(override.aircraft_id, override)
    })

    // Calculate relative values
    const numericModelId = parseInt(modelId!, 10)
    const { values: relativeValues, breakdowns: relativeValueBreakdowns, missingABB } = await calculateRelativeValues(
      allAircraftIds,
      numericModelId,
      overridesMap,
      options
    )

    // Fetch transaction overrides for sold aircraft
    let transactionOverrides = new Map<string, TransactionOverride>()
    const transactionIds = marketData.soldData
      .map(item => item.aircraftTransaction?.transaction_id)
      .filter((id): id is string => !!id)

    if (transactionIds.length > 0) {
      try {
        const { TransactionOverridesNewService } = await import('../services/transaction-overrides-new.service')
        transactionOverrides = await TransactionOverridesNewService.getByTransactionIds(transactionIds)
      } catch (error) {
        // Error fetching transaction overrides for PDF
      }
    }

    // Collect all option IDs from transaction overrides and aircraftTransactions
    const allTransactionOptionIds = new Set<string>()
    marketData.soldData.forEach(item => {
      const transactionId = item.aircraftTransaction?.transaction_id
      if (transactionId) {
        const override = transactionOverrides.get(transactionId)
        const optionIds = override?.options || item.aircraftTransaction?.options || []
        optionIds.forEach((id: string) => allTransactionOptionIds.add(id))
      }
    })

    // Fetch option details for transaction options
    let transactionOptionDetails = new Map<string, { name: string; value: number }>()
    if (allTransactionOptionIds.size > 0) {
      try {
        const { data: optionsData, error: optionsError } = await supabase
          .from('options')
          .select('option_id, name, value')
          .in('option_id', Array.from(allTransactionOptionIds))

        if (!optionsError && optionsData) {
          optionsData.forEach((opt: any) => {
            transactionOptionDetails.set(opt.option_id, { name: opt.name, value: opt.value })
          })
        }
      } catch (error) {
        // Error fetching transaction option details for PDF
      }
    }

    // Fetch manufacturer from AircraftMake via model's make_id
    let manufacturer: string | null = null
    try {
      const { AircraftModelsService } = await import('../services/intellijetnet/aircraft-models.service')
      const modelData = await AircraftModelsService.getById(numericModelId)
      if (modelData?.make_id) {
        const makeInfo = await AircraftMakesService.getById(modelData.make_id)
        manufacturer = makeInfo?.manufacturer || null
      }
    } catch (error) {
      // Error fetching manufacturer for PDF
    }

    // Calculate market statistics
    const allAircraftForStats = [
      ...marketData.forSaleAircraft,
      ...marketData.forSaleOffMarket.map(item => item.aircraft)
    ]
    const marketStatistics = calculateMarketStatistics(transactions || [], historicalMonths, allAircraftForStats)

    return {
      tables,
      overrides: overridesMap,
      options,
      relativeValues,
      relativeValueBreakdowns,
      missingABB,
      exportOptions,
      companies,
      transactionOverrides,
      transactionOptionDetails,
      manufacturer,
      marketStatistics
    }
  }, [modelId, modelInfo, aircraft, recentlySoldAircraft, removedFromMarketAircraft, offMarketAircraftData, filteredOffMarketForSale, hiddenIdentifiers, transactions])

  const handlePreviewPDF = useCallback(async (exportOptions: ExportOptions) => {
    try {
      const data = await prepareExportData(exportOptions)
      if (data) {
        setPreviewData(data)
        setShowExportOptionsModal(false)
        setShowPDFPreviewModal(true)
      }
    } catch (error) {
      // Close the modal even on error to avoid being stuck
      setShowExportOptionsModal(false)
    }
  }, [prepareExportData])

  const handleDownloadFromPreview = useCallback(async (
    editedData: Map<number, { owner?: string; broker?: string }>
  ) => {
    if (!previewData) return

    try {
      const { PDFExportService } = await import('../services/pdf-export.service')

      await PDFExportService.exportMarketSummaryToPDF({
        modelName: modelInfo?.model_name || '',
        makeName: modelInfo?.make_name || '',
        manufacturer: previewData.manufacturer,
        marketStatistics: previewData.marketStatistics,
        tables: previewData.tables,
        overrides: previewData.overrides,
        options: previewData.options,
        relativeValues: previewData.relativeValues,
        missingABB: previewData.missingABB,
        exportOptions: previewData.exportOptions,
        customColumns: customColumns,
        companies: previewData.companies,
        editedData: editedData
      })

      setShowPDFPreviewModal(false)
    } catch (error) {
      // Error downloading PDF
    }
  }, [previewData, modelInfo, customColumns])

  const handleUpdateOptionsFromPreview = useCallback(async (newOptions: ExportOptions) => {
    try {
      const data = await prepareExportData(newOptions)
      if (data) {
        setPreviewData(data)
      }
    } catch (error) {
      // Error updating PDF preview with new options
    }
  }, [prepareExportData])

  return {
    showExportOptionsModal,
    setShowExportOptionsModal,
    showPDFPreviewModal,
    setShowPDFPreviewModal,
    previewData,
    handleExportPDF,
    handlePreviewPDF,
    handleDownloadFromPreview,
    handleUpdateOptionsFromPreview,
  }
}